import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { DiscoverFeedScroll, DiscoverFeedItem, DiscoverShell } from '../../discover.styles';
import { useDiscoverActiveIndex } from '../../hooks/useDiscoverActiveIndex';
import { useDiscoverFeedLoadMore } from '../../hooks/useDiscoverFeedLoadMore';
import { useDiscoverFeedNavigation } from '../../hooks/useDiscoverFeedNavigation';
import DiscoverFeedCardSkeleton from '../discoverFeedCardSkeleton/DiscoverFeedCardSkeleton';
import DiscoverSwipeHint from '../discoverSwipeHint/DiscoverSwipeHint';
import DiscoverCard from '../discoverCard/DiscoverCard';

const SWIPE_THRESHOLD_PX = 48;

const DiscoverFeed = ({ persons, onSelectProfile }) => {
	const t = useLanguageData('discover');
	const scrollRef = useRef(null);
	const feedRef = useRef(null);
	const touchStartYRef = useRef(0);
	const feedSessionKey = useMemo(
		() => persons.map((person) => person._id).join('|'),
		[persons],
	);
	const { feedItems } = useDiscoverFeedLoadMore(
		persons,
		feedSessionKey,
	);
	const activeIndex = useDiscoverActiveIndex(scrollRef, feedRef, feedItems.length);
	const { goToPrevious, goToNext } = useDiscoverFeedNavigation({
		feedRef,
		feedItems,
		activeIndex,
	});
	const [swipeHintDismissed, setSwipeHintDismissed] = useState(false);
	const showSwipeHint =
		persons.length > 1
		&& activeIndex === 0
		&& !swipeHintDismissed;
	const showKeyboardNav = feedItems.length > 1;

	useEffect(() => {
		if (!showKeyboardNav) {
			return undefined;
		}

		const handleKeyDown = (event) => {
			if (event.key === 'ArrowUp') {
				event.preventDefault();
				goToPrevious();
				setSwipeHintDismissed(true);
				return;
			}

			if (event.key === 'ArrowDown') {
				event.preventDefault();
				goToNext();
				setSwipeHintDismissed(true);
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [goToNext, goToPrevious, showKeyboardNav]);

	useEffect(() => {
		const scroll = scrollRef.current;

		if (!scroll || persons.length <= 1) {
			return undefined;
		}

		const handleTouchStart = (event) => {
			touchStartYRef.current = event.touches[0]?.clientY ?? 0;
		};

		const handleTouchEnd = (event) => {
			const endY = event.changedTouches[0]?.clientY ?? 0;
			const deltaY = touchStartYRef.current - endY;

			if (deltaY > SWIPE_THRESHOLD_PX) {
				goToNext();
				setSwipeHintDismissed(true);
				return;
			}

			if (deltaY < -SWIPE_THRESHOLD_PX) {
				goToPrevious();
				setSwipeHintDismissed(true);
			}
		};

		scroll.addEventListener('touchstart', handleTouchStart, { passive: true });
		scroll.addEventListener('touchend', handleTouchEnd, { passive: true });

		return () => {
			scroll.removeEventListener('touchstart', handleTouchStart);
			scroll.removeEventListener('touchend', handleTouchEnd);
		};
	}, [goToNext, goToPrevious, persons.length, scrollRef]);

	return (
		<DiscoverShell ref={scrollRef}>
			<DiscoverSwipeHint
				scrollRef={scrollRef}
				visible={showSwipeHint}
				onDismiss={() => setSwipeHintDismissed(true)}
			/>
			<DiscoverFeedScroll ref={feedRef} aria-label={t.ariaLabel}>
				{feedItems.map((item, index) => {
					if (item.type === 'skeleton') {
						return (
							<DiscoverFeedItem key={item.key} data-feed-item-key={item.key}>
								<DiscoverFeedCardSkeleton />
							</DiscoverFeedItem>
						);
					}

					return (
						<DiscoverFeedItem key={item.key} data-feed-item-key={item.key}>
							<DiscoverCard
								profile={item.profile}
								isActive={activeIndex === index}
								onClick={onSelectProfile}
							/>
						</DiscoverFeedItem>
					);
				})}
			</DiscoverFeedScroll>
		</DiscoverShell>
	);
};

export default DiscoverFeed;
